/*
 * Feature: browser-side validation for chat frontend action events.
 * Implementation notes: mirrors the API's closed payload contract before dispatching event-bus or external-browser behavior.
 * Recent changes: rejects suggested-contact IDs, unknown properties, malformed dates, and unsupported actions.
 */

import { z } from 'zod';

export const FRONTEND_ACTION_NAMES = [
  'open_note_draft',
  'open_task_draft',
  'open_email_draft',
  'open_meeting_draft',
  'open_google_task',
] as const;

export type FrontendActionName = typeof FRONTEND_ACTION_NAMES[number];

const entityId = z.number().int().positive().max(98_999_999);
const bounded = (max: number) => z.string().trim().max(max);
const requiredBounded = (max: number) => bounded(max).min(1);
const pairedEntityFields = <Shape extends z.ZodRawShape>(shape: Shape) => z.object(shape).strict().superRefine((value, context) => {
  const record = value as Record<string, unknown>;
  if (Boolean(record.accountId) !== Boolean(record.accountName)) {
    context.addIssue({ code: 'custom', message: 'accountId and accountName must be supplied together' });
  }
  if (Boolean(record.contactId) !== Boolean(record.contactName)) {
    context.addIssue({ code: 'custom', message: 'contactId and contactName must be supplied together' });
  }
});

const notePayload = z.object({
  accountId: entityId.optional(),
  contactId: entityId.optional(),
  teamId: entityId.optional(),
  content: bounded(4_000).optional(),
}).strict();

const taskPayload = pairedEntityFields({
  title: requiredBounded(200),
  description: bounded(4_000).optional(),
  dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  }).optional(),
  priority: z.enum(['Low', 'Medium', 'High']).optional(),
  accountId: entityId.optional(),
  accountName: requiredBounded(200).optional(),
  contactId: entityId.optional(),
  contactName: requiredBounded(200).optional(),
});

const emailList = z.array(z.string().trim().email().max(320)).max(20);
const emailPayload = z.object({
  to: emailList.optional(),
  cc: emailList.optional(),
  bcc: emailList.optional(),
  subject: bounded(200).optional(),
  body: bounded(10_000).optional(),
}).strict();

const meetingPayload = z.object({
  title: requiredBounded(200),
  description: bounded(4_000).optional(),
  location: bounded(300).optional(),
  start: z.string().datetime({ offset: true }).optional(),
  end: z.string().datetime({ offset: true }).optional(),
}).strict().superRefine((value, context) => {
  if (Boolean(value.start) !== Boolean(value.end)) {
    context.addIssue({ code: 'custom', message: 'start and end must be supplied together' });
  } else if (value.start && value.end && Date.parse(value.end) <= Date.parse(value.start)) {
    context.addIssue({ code: 'custom', message: 'end must be later than start' });
  }
});

const googleTaskPayload = z.object({
  title: requiredBounded(200),
  notes: bounded(4_000).optional(),
  dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  }).optional(),
}).strict();

const baseEnvelope = {
  type: z.literal('client.action'),
  actionId: z.string().trim().min(1).max(128),
};

const clientActionEventSchema = z.discriminatedUnion('action', [
  z.object({ ...baseEnvelope, action: z.literal('open_note_draft'), payload: notePayload }).strict(),
  z.object({ ...baseEnvelope, action: z.literal('open_task_draft'), payload: taskPayload }).strict(),
  z.object({ ...baseEnvelope, action: z.literal('open_email_draft'), payload: emailPayload }).strict(),
  z.object({ ...baseEnvelope, action: z.literal('open_meeting_draft'), payload: meetingPayload }).strict(),
  z.object({ ...baseEnvelope, action: z.literal('open_google_task'), payload: googleTaskPayload }).strict(),
]);

export type ClientActionEvent = z.infer<typeof clientActionEventSchema>;

export function parseClientActionEvent(value: unknown): ClientActionEvent | null {
  const parsed = clientActionEventSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function formatGoogleTaskClipboardText(payload: z.infer<typeof googleTaskPayload>): string {
  return [
    `Title: ${payload.title}`,
    ...(payload.notes?.trim() ? [`Notes: ${payload.notes.trim()}`] : []),
    ...(payload.dueDate ? [`Due: ${payload.dueDate}`] : []),
  ].join('\n');
}
